import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from './ui/button';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VideoRating = ({ videoId, likes = 0, dislikes = 0, compact = false }) => {
  const { user } = useAuth();
  const [myVote, setMyVote] = useState(null);
  const [counts, setCounts] = useState({ likes, dislikes });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setCounts({ likes, dislikes });
  }, [likes, dislikes]);

  useEffect(() => {
    if (user && videoId) {
      axios.get(`${API}/youtube/videos/${videoId}/my-vote`)
        .then(res => setMyVote(res.data.vote))
        .catch(() => {});
    }
  }, [user, videoId]);

  const handleVote = async (vote) => {
    if (!user) {
      toast.error('Sign in to rate videos');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/youtube/videos/${videoId}/rate`, { vote });
      const newVote = res.data.vote;
      setMyVote(newVote);

      // Update local counts
      if (res.data.status === 'removed') {
        setCounts(prev => ({
          likes: prev.likes - (vote === 'like' ? 1 : 0),
          dislikes: prev.dislikes - (vote === 'dislike' ? 1 : 0),
        }));
      } else if (res.data.status === 'switched') {
        setCounts(prev => ({
          likes: prev.likes + (vote === 'like' ? 1 : -1),
          dislikes: prev.dislikes + (vote === 'dislike' ? 1 : -1),
        }));
      } else {
        setCounts(prev => ({
          likes: prev.likes + (vote === 'like' ? 1 : 0),
          dislikes: prev.dislikes + (vote === 'dislike' ? 1 : 0),
        }));
      }
    } catch {
      toast.error('Failed to rate');
    } finally {
      setLoading(false);
    }
  };

  const net = counts.likes - counts.dislikes;

  if (compact) {
    return (
      <div className="flex items-center gap-1" data-testid={`video-rating-${videoId}`}>
        <Button
          variant="ghost"
          size="icon"
          className={`h-7 w-7 ${myVote === 'like' ? 'text-green-600 bg-green-50' : ''}`}
          onClick={() => handleVote('like')}
          disabled={loading}
          data-testid={`like-btn-${videoId}`}
        >
          <ThumbsUp className={`w-3.5 h-3.5 ${myVote === 'like' ? 'fill-current' : ''}`} />
        </Button>
        <span className={`text-xs font-medium min-w-[20px] text-center ${net > 0 ? 'text-green-600' : net < 0 ? 'text-red-500' : 'text-muted-foreground'}`}>
          {counts.likes > 0 ? counts.likes : ''}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className={`h-7 w-7 ${myVote === 'dislike' ? 'text-red-500 bg-red-50' : ''}`}
          onClick={() => handleVote('dislike')}
          disabled={loading}
          data-testid={`dislike-btn-${videoId}`}
        >
          <ThumbsDown className={`w-3.5 h-3.5 ${myVote === 'dislike' ? 'fill-current' : ''}`} />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2" data-testid={`video-rating-${videoId}`}>
      <Button
        variant={myVote === 'like' ? 'default' : 'outline'}
        size="sm"
        className={`h-8 ${myVote === 'like' ? 'bg-green-600 hover:bg-green-700' : ''}`}
        onClick={() => handleVote('like')}
        disabled={loading}
        data-testid={`like-btn-${videoId}`}
      >
        <ThumbsUp className={`w-4 h-4 mr-1 ${myVote === 'like' ? 'fill-current' : ''}`} />
        {counts.likes}
      </Button>
      <Button
        variant={myVote === 'dislike' ? 'default' : 'outline'}
        size="sm"
        className={`h-8 ${myVote === 'dislike' ? 'bg-red-500 hover:bg-red-600' : ''}`}
        onClick={() => handleVote('dislike')}
        disabled={loading}
        data-testid={`dislike-btn-${videoId}`}
      >
        <ThumbsDown className={`w-4 h-4 mr-1 ${myVote === 'dislike' ? 'fill-current' : ''}`} />
        {counts.dislikes}
      </Button>
    </div>
  );
};

export default VideoRating;
